/**
 * DateTime utility functions for ORO app
 *
 * Provides consistent date/time formatting, validation, and manipulation
 * across the application.
 */

/**
 * Formats a timestamp for display in event headers and cards
 */
export function formatEventDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a timestamp for time display in the browser's local timezone,
 * with the timezone abbreviation appended. (#1655 — closes the
 * notification/app mismatch where the SMS said "8:30 PM EDT" and the app
 * said "7:30 PM" with no bridge.)
 */
export function formatEventTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Formats timestamp for compact display (used in cards)
 */
export function formatCompactDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  // #2259: bucket by LOCAL CALENDAR DAY, not elapsed-ms division — a live
  // event that started 30 minutes ago is still "Today", not "Yesterday".
  // Math.round absorbs DST-transition days (23h/25h ≠ exact 24h multiples).
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round(
    (startOfDay(date) - startOfDay(now)) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays < 7 && diffDays > 0) return date.toLocaleDateString('en-US', { weekday: 'long' });
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Formats time for compact display, with browser-local tz abbreviation. (#1655)
 */
export function formatCompactTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Formats date range for events
 */
export function formatDateRange(startTimestamp: number, endTimestamp?: number): string {
  const startDate = formatCompactDate(startTimestamp);
  const startTime = formatCompactTime(startTimestamp);
  
  if (!endTimestamp) {
    return `${startDate} at ${startTime}`;
  }
  
  const endDate = formatCompactDate(endTimestamp);
  const endTime = formatCompactTime(endTimestamp);
  
  // Same day event
  if (startDate === endDate) {
    return `${startDate}, ${startTime} - ${endTime}`;
  }
  
  // Multi-day event
  return `${startDate} ${startTime} - ${endDate} ${endTime}`;
}

/**
 * Checks if an event is happening today
 */
export function isToday(timestamp: number): boolean {
  const date = new Date(timestamp * 1000);
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

/**
 * Checks if an event is in the past
 */
export function isPast(timestamp: number): boolean {
  const now = Date.now();
  return timestamp * 1000 < now;
}

/**
 * Checks if an event is upcoming (in future)
 */
export function isUpcoming(timestamp: number): boolean {
  return !isPast(timestamp);
}

/**
 * Buffer applied after an event's effective end time before it is treated as
 * past for upcoming/past list splits. Covers events still happening (no
 * ends_at set), post-event social/wrap time, and late arrivals navigating to
 * the check-in surface. See #1782.
 */
export const POST_EVENT_VISIBILITY_BUFFER_SECONDS = 60 * 60;

/**
 * Returns true when an event should be treated as past for upcoming/past
 * list splits and check-in visibility. Prefers ends_at when present, falls
 * back to starts_at, and applies POST_EVENT_VISIBILITY_BUFFER_SECONDS so
 * events still happening or recently ended remain in upcoming. See #1782.
 */
export function isPastEvent(event: { starts_at: number; ends_at?: number | null }): boolean {
  const now = Math.floor(Date.now() / 1000);
  const effectiveEnd = event.ends_at ?? event.starts_at;
  return effectiveEnd + POST_EVENT_VISIBILITY_BUFFER_SECONDS < now;
}

export type EventTimingStatus = 'scheduled' | 'live' | 'starting-soon' | 'ended';

export interface EventTimingResult {
  status: EventTimingStatus;
  label: string;
}

/**
 * Classifies an event by start/end timestamps for the card timing badge
 * (live / starting-soon / ended / scheduled). Consolidated here from the
 * web and native EventCard copies, which had micro-diverged (#2150).
 */
export function getEventTimingStatus(
  startsAt?: number,
  endsAt?: number | null,
): EventTimingResult {
  if (!startsAt) return { status: 'scheduled', label: 'Scheduled' };

  const now = Date.now() / 1000;

  // Event has ended
  if (endsAt != null && now > endsAt) {
    return { status: 'ended', label: 'Ended' };
  }

  // Event is currently happening
  if (now >= startsAt && (endsAt == null || now <= endsAt)) {
    return { status: 'live', label: 'Live' };
  }

  // Event starts within the next hour
  if (startsAt - now <= 3600) {
    const minutesUntil = Math.max(0, Math.floor((startsAt - now) / 60));
    return { status: 'starting-soon', label: `Starts in ${minutesUntil}m` };
  }

  return { status: 'scheduled', label: 'Scheduled' };
}

/**
 * Gets relative time string (e.g., "in 2 hours", "3 days ago")
 */
export function getRelativeTime(timestamp: number): string {
  const now = Date.now();
  const eventTime = timestamp * 1000;
  const diffMs = eventTime - now;
  const diffMinutes = Math.floor(Math.abs(diffMs) / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  const isInPast = diffMs < 0;
  const suffix = isInPast ? 'ago' : 'from now';
  
  if (diffMinutes < 1) return 'now';
  if (diffMinutes < 60) return `${diffMinutes}m ${suffix}`;
  if (diffHours < 24) return `${diffHours}h ${suffix}`;
  return `${diffDays}d ${suffix}`;
}

/**
 * Converts Date to timestamp (seconds)
 */
export function dateToTimestamp(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}

/**
 * Converts timestamp (seconds) to Date
 */
export function timestampToDate(timestamp: number): Date {
  return new Date(timestamp * 1000);
}

/**
 * Gets smart default time for new events (next hour, rounded)
 */
export function getDefaultEventTime(): Date {
  const now = new Date();
  const nextHour = new Date(now);
  nextHour.setHours(now.getHours() + 1, 0, 0, 0); // Round to next hour
  return nextHour;
}

/**
 * Validates that end time is after start time
 */
export function validateTimeRange(startTime: Date, endTime?: Date): string | null {
  if (!endTime) return null;
  
  if (endTime <= startTime) {
    return 'End time must be after start time';
  }
  
  const diffHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
  if (diffHours > 24) {
    return 'Event duration cannot exceed 24 hours';
  }
  
  return null;
}